import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { Play, RefreshCw, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { FormActions } from "@jbnl/shared-ui";
import { Alert, AlertList } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
  type DataTableColumns,
} from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  Filterdescriptor,
  Filterstand,
} from "@/components/ui/data-table-filter";
import { Pager } from "@/components/ui/pager";
import { ListDetail } from "@/components/ListDetail";
import { FULL_PAGE_SIZE, toNumber } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  useWedstrijddagen,
  type Seizoen,
} from "@/features/wedstrijden/useWedstrijden";
import {
  alsMinuten,
  alsReistijd,
  onberekend,
  useClearReistijd,
  useHerberekenReistijden,
  useKorpsenZonderCoordinaten,
  useReistijden,
  useReistijdenVoorDag,
  useSetReistijd,
  type Reistijd,
} from "./useReistijden";

/** `korpsName` is unmapped on purpose: it is the server's default, so an unknown `sortBy` is right. */
const VOOR_DAG_SORT_BY: Record<string, string> = { reistijd: "seconds" };

const helper = createColumnHelper<typeof dataTableFeatures, Reistijd>();

/** True from the moment a fill is queued until the uncomputed count first moves off `wachtVanaf`. */
function wachtOpEersteVoortgang(
  wachtVanaf: number | null,
  reistijden: Reistijd[] | undefined,
) {
  return wachtVanaf !== null && onberekend(reistijden) === wachtVanaf;
}

/**
 * Exists for a cost ADR-0013 accepted rather than solved: an override is keyed by coordinate
 * pair, so new coordinates in Portaal silently revert it. Showing which are hand-set is the
 * whole mitigation. A dag at a time, because the whole matrix is some 1,540 rows.
 */
export function Reistijden({
  seizoen,
  dagId,
  onKies,
  pagina,
  onPaginaChange,
}: {
  seizoen: Seizoen;
  dagId: string | undefined;
  onKies: (id?: string) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  // The number of uncomputed pairs a queued fill has not moved yet. Derived rather than cleared
  // by an effect: a row being written *is* the count changing, so there is no second piece of
  // state to keep in step.
  const [wachtVanaf, setWachtVanaf] = useState<number | null>(null);

  const { t } = useTranslation("reistijden");
  const { data: alleDagen, isPending } = useWedstrijddagen(seizoen.id);
  const {
    data: reistijden,
    isFetching,
    refetch,
  } = useReistijden(seizoen.id, wachtVanaf);

  const wacht = wachtOpEersteVoortgang(wachtVanaf, reistijden);

  // Only the dagen anyone can drive to. Reistijd is measured to the meldadres, which is the
  // organiserend korps's address until it is overridden (ADR-0013) - so a dag whose host has not
  // been appointed has no point to route to and never gets a row. Listing it here would put an
  // always-empty wedstrijddag on the one screen that exists to show the numbers.
  const dagen = alleDagen?.filter(
    (dag): dag is typeof dag & { organiserendKorpsName: string } =>
      dag.organiserendKorpsName !== null &&
      dag.organiserendKorpsName !== undefined,
  );

  const dag = dagen?.find((d) => d.id === dagId);

  // Named, not just counted, in the order the backend already sorted them - so a beheerder can
  // find each one. The list is short by nature: a korps without coordinates in Portaal is the
  // exception, and the ordinary season has none at all. Global to the database rather than
  // scoped to the chosen wedstrijddag - a beheerder should meet this before picking one at all.
  const { data: korpsenZonderCoordinaten } = useKorpsenZonderCoordinaten();
  const zonderPunt = korpsenZonderCoordinaten ?? [];

  return (
    <>
      <KorpsenZonderCoordinatenWaarschuwing zonderPunt={zonderPunt} t={t} />

      <Stand
        seizoenId={seizoen.id}
        reistijden={reistijden}
        bezig={isFetching}
        wacht={wacht}
        onVernieuw={() => void refetch()}
        onGevraagd={(vanaf) => {
          setWachtVanaf(vanaf);

          // Nothing exposes the job queue, so a run that never happens - Hangfire down,
          // OpenRouteService refusing the key - would otherwise poll for the rest of the
          // afternoon. Three minutes rather than one, and the reason is this job in particular:
          // it is a matrix request to somebody else's service and `ReistijdFillJob` is patient
          // by design, so a first attempt landing a minute in is ordinary rather than late.
          window.setTimeout(() => setWachtVanaf(null), 180_000);
        }}
      />

      <ListDetail
        selected={Boolean(dag)}
        backLabel={t("dagen.terug")}
        onBack={() => onKies()}
        list={
          <WedstrijddagenKaart
            isPending={isPending}
            dagen={dagen}
            dag={dag}
            t={t}
            onKies={onKies}
          />
        }
        detail={
          <GekozenDagDetail
            dag={dag}
            seizoenId={seizoen.id}
            reistijden={reistijden}
            pagina={pagina}
            onPaginaChange={onPaginaChange}
            t={t}
          />
        }
      />
    </>
  );
}

/** Above the toolbar, because only a person can make this fix in Portaal. Warning, not danger:
 *  the reistijd is usable meanwhile. */
function KorpsenZonderCoordinatenWaarschuwing({
  zonderPunt,
  t,
}: {
  zonderPunt: string[];
  t: TFunction<"reistijden">;
}) {
  if (zonderPunt.length === 0) return null;

  return (
    <AlertList
      tone="warning"
      className="mb-6"
      title={t("tabel.zonderPunt", { count: zonderPunt.length })}
      description={t("tabel.zonderPuntUitleg")}
    >
      <ul className="list-disc space-y-0.5 ps-5">
        {zonderPunt.map((naam) => (
          <li key={naam}>{naam}</li>
        ))}
      </ul>
    </AlertList>
  );
}

/** The dagen list, on the left side of the `ListDetail`. */
function WedstrijddagenKaart({
  isPending,
  dagen,
  dag,
  t,
  onKies,
}: {
  isPending: boolean;
  dagen:
    | {
        id: string;
        date: string;
        organiserendKorpsName: string;
        niveauName: string;
      }[]
    | undefined;
  dag: { id: string } | undefined;
  t: TFunction<"reistijden">;
  onKies: (id?: string) => void;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        {isPending ? (
          <DataTableSkeleton
            label={t("dagen.label")}
            columns={dagKolommen(t)}
            rows={6}
            search
          />
        ) : (
          // No `filters`: #182 still has open fog on whether this list survives at all
          // now that the wedstrijddag screen (#186-188) offers the same pick.
          <DataTable
            label={t("dagen.label")}
            data={dagen ?? []}
            columns={dagKolommen(t)}
            getRowId={(row) => row.id}
            selectedId={dag?.id}
            onSelect={onKies}
            searchPlaceholder={t("dagen.zoek")}
            empty={t("dagen.leeg")}
            clientPageSize={FULL_PAGE_SIZE}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** The reistijden table for the chosen dag, or a prompt to choose one, on the right side. */
function GekozenDagDetail({
  dag,
  seizoenId,
  reistijden,
  pagina,
  onPaginaChange,
  t,
}: {
  dag: { id: string } | undefined;
  seizoenId: string;
  reistijden: Reistijd[] | undefined;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
  t: TFunction<"reistijden">;
}) {
  if (!dag) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">{t("dagen.kies")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <ReistijdenVoorDag
      key={dag.id}
      seizoenId={seizoenId}
      wedstrijddagId={dag.id}
      alleRijen={reistijden ?? []}
      pagina={pagina}
      onPaginaChange={onPaginaChange}
    />
  );
}

/**
 * A snapshot: the fill runs out of band whenever a pair comes into existence (ADR-0013), and
 * nothing exposes the queue, so a true count beats a spinner that guesses. Berekenen means
 * herberekenen - a hand-set reistijd survives it in `ReistijdFillJob.StoreAsync`.
 */
function Stand({
  seizoenId,
  reistijden,
  bezig,
  wacht,
  onVernieuw,
  onGevraagd,
}: {
  seizoenId: string;
  reistijden: Reistijd[] | undefined;
  bezig: boolean;
  wacht: boolean;
  onVernieuw: () => void;
  onGevraagd: (vanaf: number) => void;
}) {
  const { t } = useTranslation(["reistijden", "common"]);
  const herbereken = useHerberekenReistijden(seizoenId);
  const koud = onberekend(reistijden);
  const bezigMetBerekenen = wacht || herbereken.isPending;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
      <span className="text-muted-foreground">
        {standTekst(reistijden, koud, t)}
      </span>

      {wacht && (
        <span className="text-muted-foreground">{t("stand.draait")}</span>
      )}

      {herbereken.error && (
        <span className="text-destructive">{herbereken.error.message}</span>
      )}

      <div className="ms-auto flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onVernieuw} disabled={bezig}>
          <RefreshCw aria-hidden />
          {t("stand.vernieuwen")}
        </Button>

        <BerekenenKnop
          bezig={bezigMetBerekenen}
          onClick={() =>
            herbereken.mutate(undefined, {
              onSuccess: (rijen) => onGevraagd(onberekend(rijen)),
            })
          }
          t={t}
        />
      </div>
    </div>
  );
}

function standTekst(
  reistijden: Reistijd[] | undefined,
  koud: number,
  t: TFunction<["reistijden", "common"]>,
) {
  if (reistijden === undefined) return t("stand.ophalen");
  if (koud === 0) return t("stand.alleBerekend", { aantal: reistijden.length });
  return t("stand.nogNiet", { koud, aantal: reistijden.length });
}

/** Always enabled, because it recomputes every pair; busy only while a run is on its way. */
function BerekenenKnop({
  bezig,
  onClick,
  t,
}: {
  bezig: boolean;
  onClick: () => void;
  t: TFunction<["reistijden", "common"]>;
}) {
  return (
    <Button size="sm" disabled={bezig} onClick={onClick}>
      <Play aria-hidden />
      {bezig ? t("common:actions.busy") : t("stand.berekenen")}
    </Button>
  );
}

/**
 * The accessor returns `dd-MM-yyyy` so the search box matches the column, but that sorts 01-06
 * before 16-05. The comparison reads the ISO date underneath instead.
 */
const opDatum = (
  a: { original: { date: string } },
  b: { original: { date: string } },
) => a.original.date.localeCompare(b.original.date);

const dagKolommen = (t: TFunction<"reistijden">) =>
  createColumnHelper<
    typeof dataTableFeatures,
    {
      id: string;
      date: string;
      organiserendKorpsName: string;
      niveauName: string;
    }
  >().columns([
    {
      id: "datum",
      header: t("dagen.datum"),
      accessorFn: (dag) => datumKort(dag.date),
      sortFn: opDatum,
      // As on the wedstrijddagen list: a date is one word, however narrow the pane gets.
      cell: (info) => (
        <span className="whitespace-nowrap">{info.getValue()}</span>
      ),
    },
    {
      id: "korps",
      header: t("dagen.korps"),
      accessorKey: "organiserendKorpsName",
    },
    { id: "niveau", header: t("dagen.niveau"), accessorKey: "niveauName" },
  ]);

function sorteerVeld(sort: SortingState[number] | undefined) {
  return sort && VOOR_DAG_SORT_BY[sort.id];
}

type ReistijdenVoorDagResultaat = NonNullable<
  ReturnType<typeof useReistijdenVoorDag>["data"]
>;

function ReistijdenVoorDag({
  seizoenId,
  wedstrijddagId,
  alleRijen,
  pagina,
  onPaginaChange,
}: {
  seizoenId: string;
  wedstrijddagId: string;
  alleRijen: Reistijd[];
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation("reistijden");
  const [teWijzigen, setTeWijzigen] = useState<Reistijd | null>(null);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = sorting[0];

  // Server-driven like the rest of this table (~1.540 rijen per dag): no `past`, so the rail
  // shows no counts either - see the map's decision on that.
  const descriptors = useMemo<Filterdescriptor<Reistijd>[]>(
    () => [
      {
        id: "bron",
        label: t("tabel.bron"),
        kind: "meerkeuze",
        opties: [
          { waarde: "handmatig", label: t("tabel.filter.handmatig") },
          { waarde: "berekend", label: t("tabel.filter.berekend") },
          { waarde: "geen", label: t("tabel.filter.geen") },
        ],
      },
    ],
    [t],
  );
  const { stand, onStand } = useFilterStand(descriptors, "/reistijden/korpsen");
  const bron =
    stand.bron?.soort === "meerkeuze" ? stand.bron.waarden : undefined;

  const { data: result, isPending } = useReistijdenVoorDag(
    wedstrijddagId,
    pagina,
    FULL_PAGE_SIZE,
    search,
    sorteerVeld(sort),
    sort?.desc ?? false,
    bron,
  );
  const rijen = result?.page.items ?? [];

  const kolommen = helper.columns([
    helper.accessor("korpsName", { header: t("tabel.korps") }),
    helper.accessor((rij) => rij.seconds, {
      id: "reistijd",
      header: t("tabel.reistijd"),
      cell: (info) => <ReistijdWaarde seconds={info.getValue()} t={t} />,
    }),
    helper.accessor((rij) => rij.bron, {
      id: "bron",
      header: t("tabel.bron"),
      cell: (info) => (
        <BronWaarde
          seconds={info.row.original.seconds}
          isHandmatig={info.row.original.isHandmatig}
          t={t}
        />
      ),
    }),
  ]);

  return (
    <>
      <Card>
        <CardContent className="grid gap-4 pt-6">
          <ReistijdenTabelInhoud
            exportLink={exportHref(
              "/api/beheer/wedstrijddagen/{wedstrijddagId}/reistijden/export",
              {
                path: { wedstrijddagId },
                query: {
                  search,
                  sortBy: sorteerVeld(sort),
                  sortDescending: sort?.desc ?? false,
                  bron,
                },
              },
            )}
            result={result}
            isPending={isPending}
            kolommen={kolommen}
            rijen={rijen}
            sorting={sorting}
            onSortingChange={setSorting}
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              onPaginaChange(1);
            }}
            onSelect={(id) =>
              setTeWijzigen(
                rijen.find((r) => `${r.korpsId}-${r.wedstrijddagId}` === id) ??
                  null,
              )
            }
            onPaginaChange={onPaginaChange}
            t={t}
            descriptors={descriptors}
            stand={stand}
            onStand={onStand}
          />
        </CardContent>
      </Card>

      <ReistijdDialoog
        seizoenId={seizoenId}
        rij={teWijzigen}
        alleRijen={alleRijen}
        onSluit={() => setTeWijzigen(null)}
      />
    </>
  );
}

function ReistijdWaarde({
  seconds,
  t,
}: {
  seconds: number | null;
  t: TFunction<"reistijden">;
}) {
  if (seconds == null) {
    return (
      <span className="text-muted-foreground">
        {t("tabel.nogNietBerekend")}
      </span>
    );
  }

  return <span className="font-medium">{alsReistijd(seconds)}</span>;
}

/**
 * Nothing at all for a pair with no row yet. "Berekend" beside "nog niet berekend" is a
 * straight contradiction, and this column's whole job is to be believed: it is the only place
 * a hand-set reistijd that has silently reverted becomes visible.
 */
function BronWaarde({
  seconds,
  isHandmatig,
  t,
}: {
  seconds: number | null;
  isHandmatig: boolean;
  t: TFunction<"reistijden">;
}) {
  if (seconds == null) {
    return <span className="text-muted-foreground">{t("tabel.geen")}</span>;
  }

  return isHandmatig ? (
    <span className="font-medium">{t("tabel.handmatig")}</span>
  ) : (
    <span className="text-muted-foreground">{t("tabel.berekend")}</span>
  );
}

function ReistijdenTabelInhoud({
  result,
  isPending,
  kolommen,
  rijen,
  sorting,
  onSortingChange,
  search,
  onSearchChange,
  onSelect,
  onPaginaChange,
  t,
  descriptors,
  stand,
  onStand,
  exportLink,
}: {
  result: ReistijdenVoorDagResultaat | undefined;
  isPending: boolean;
  kolommen: DataTableColumns<Reistijd>;
  rijen: Reistijd[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string) => void;
  onPaginaChange: (pagina: number) => void;
  t: TFunction<"reistijden">;
  descriptors: Filterdescriptor<Reistijd>[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  exportLink: string;
}) {
  return (
    <>
      {/* Information, not a warning: this resolves itself on the background run, so there is
        nothing to act on. Describes the whole dag, not the page on screen. */}
      {result && toNumber(result.nietBerekendCount) > 0 && (
        <Alert tone="info">
          {t("tabel.nietBerekend", {
            count: toNumber(result.nietBerekendCount),
          })}
        </Alert>
      )}

      {isPending || !result ? (
        <DataTableSkeleton
          label={t("tabel.label")}
          columns={kolommen}
          rows={6}
          search
        />
      ) : (
        <>
          {/*
            The row is the control, which is what this table is for - so choosing a korps
            opens the editor rather than a cell holding its own button.
          */}
          <DataTable
            label={t("tabel.label")}
            data={rijen}
            columns={kolommen}
            getRowId={(row) => `${row.korpsId}-${row.wedstrijddagId}`}
            onSelect={onSelect}
            searchPlaceholder={t("tabel.zoek")}
            empty={t("tabel.leeg")}
            exportHref={exportLink}
            serverDriven={{
              sorting,
              onSortingChange,
              globalFilter: search,
              // A search narrows the set the server counts, so page 2 of the old set would
              // otherwise land past the end of the new, shorter one.
              onGlobalFilterChange: onSearchChange,
            }}
            filters={{
              descriptors,
              stand,
              onStand,
              // No counts: this table is `serverDriven`, so nothing here can count what it
              // has not fetched - see the map's decision on that.
              toonTelling: false,
            }}
          />
          <Pager
            page={result.page.page}
            pageSize={result.page.pageSize}
            totalCount={result.page.totalCount}
            onPageChange={onPaginaChange}
          />
        </>
      )}
    </>
  );
}

/**
 * Typing a reistijd by hand. Minutes, because that is the unit a beheerder thinks in - the store
 * keeps seconds so a hand-set row and a computed one sort against each other unchanged.
 */
function ReistijdDialoog({
  seizoenId,
  rij,
  alleRijen,
  onSluit,
}: {
  seizoenId: string;
  rij: Reistijd | null;
  alleRijen: Reistijd[];
  onSluit: () => void;
}) {
  const { t } = useTranslation(["reistijden", "common"]);
  const set = useSetReistijd(seizoenId);
  const clear = useClearReistijd(seizoenId);
  const [minuten, setMinuten] = useState("");

  // Same stored row, different wedstrijddag. Empty is the ordinary case.
  const gedeeldeDagen = gedeeldeDagenVoor(rij, alleRijen);

  function sluit() {
    set.reset();
    clear.reset();
    setMinuten("");
    onSluit();
  }

  return (
    <Dialog open={rij !== null} onOpenChange={(open) => !open && sluit()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dialoog.title")}</DialogTitle>
          <DialogDescription>
            {t("dialoog.description", {
              korps: rij?.korpsName ?? "",
              dag: rij?.wedstrijddagLabel ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!rij) return;

            set.mutate(
              {
                korpsId: rij.korpsId,
                wedstrijddagId: rij.wedstrijddagId,
                minutes: Number(minuten),
              },
              { onSuccess: sluit },
            );
          }}
        >
          {/* Keyed by a pair of points, not records: dagen sharing a meldadres are one journey, so
            editing changes all of them. Startling on a screen that lists them separately. */}
          <GedeeldeDagenWaarschuwing gedeeldeDagen={gedeeldeDagen} t={t} />

          <ReistijdMinutenVeld
            minuten={minuten}
            onMinutenChange={setMinuten}
            placeholder={minutenPlaceholder(rij)}
            t={t}
          />

          <ReistijdDialoogActies
            rij={rij}
            set={set}
            clear={clear}
            onTerugzetten={(korpsId, wedstrijddagId) =>
              clear.mutate({ korpsId, wedstrijddagId }, { onSuccess: sluit })
            }
            t={t}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReistijdMinutenVeld({
  minuten,
  onMinutenChange,
  placeholder,
  t,
}: {
  minuten: string;
  onMinutenChange: (value: string) => void;
  placeholder: string;
  t: TFunction<["reistijden", "common"]>;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor="reistijd-minuten">{t("dialoog.minuten")}</Label>
      <Input
        id="reistijd-minuten"
        type="number"
        min={1}
        max={480}
        required
        value={minuten}
        onChange={(event) => onMinutenChange(event.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function gedeeldeDagenVoor(rij: Reistijd | null, alleRijen: Reistijd[]) {
  return rij
    ? alleRijen.filter(
        (r) =>
          r.paarSleutel === rij.paarSleutel &&
          r.wedstrijddagId !== rij.wedstrijddagId,
      )
    : [];
}

function minutenPlaceholder(rij: Reistijd | null) {
  return rij?.seconds == null ? "" : String(alsMinuten(rij.seconds));
}

function GedeeldeDagenWaarschuwing({
  gedeeldeDagen,
  t,
}: {
  gedeeldeDagen: Reistijd[];
  t: TFunction<["reistijden", "common"]>;
}) {
  if (gedeeldeDagen.length === 0) return null;

  return (
    <AlertList
      tone="warning"
      title={t("dialoog.gedeeld", { count: gedeeldeDagen.length })}
      description={t("dialoog.gedeeldUitleg")}
    >
      <ul className="list-disc space-y-0.5 ps-5">
        {gedeeldeDagen.map((dag) => (
          <li key={dag.wedstrijddagId}>{dag.wedstrijddagLabel}</li>
        ))}
      </ul>
    </AlertList>
  );
}

function ReistijdDialoogActies({
  rij,
  set,
  clear,
  onTerugzetten,
  t,
}: {
  rij: Reistijd | null;
  set: ReturnType<typeof useSetReistijd>;
  clear: ReturnType<typeof useClearReistijd>;
  onTerugzetten: (korpsId: string, wedstrijddagId: string) => void;
  t: TFunction<["reistijden", "common"]>;
}) {
  return (
    <>
      <FormActions>
        <TerugzettenKnop
          rij={rij}
          clear={clear}
          onTerugzetten={onTerugzetten}
          t={t}
        />

        <Button type="submit" disabled={set.isPending}>
          {set.isPending ? t("common:actions.busy") : t("common:actions.save")}
        </Button>
      </FormActions>

      {set.error && (
        <p className="text-sm text-destructive">{set.error.message}</p>
      )}
      {clear.error && (
        <p className="text-sm text-destructive">{clear.error.message}</p>
      )}
    </>
  );
}

/** Deletes the row rather than unflagging it, so the pair goes back on the fill run's list
 *  instead of sitting there as though the engine produced it. */
function TerugzettenKnop({
  rij,
  clear,
  onTerugzetten,
  t,
}: {
  rij: Reistijd | null;
  clear: ReturnType<typeof useClearReistijd>;
  onTerugzetten: (korpsId: string, wedstrijddagId: string) => void;
  t: TFunction<["reistijden", "common"]>;
}) {
  if (!rij?.isHandmatig) return null;

  return (
    <Button
      type="button"
      variant="secondary"
      disabled={clear.isPending}
      onClick={() => onTerugzetten(rij.korpsId, rij.wedstrijddagId)}
    >
      <RotateCcw aria-hidden className="size-4" />
      {clear.isPending ? t("common:actions.busy") : t("dialoog.terugzetten")}
    </Button>
  );
}
